import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../../mesh/guards';
import type {
    SketchArcObject,
    SketchCircleObject,
    SketchEllipseObject,
    SketchLineObject,
    SketchParabolaObject,
    SketchSplineObject,
    SketchStyleSplineObject,
} from '../../../mesh/sketch';
import type {SketchGroup} from '../../../mesh/sketch/SketchGroup';
import type {ReferencePlane} from '../../../reference';
import {CombineSketchEntitiesUnifiedDirection} from '../../offset/CombineSketchEntitiesUnifiedDirection';
import {SketchEntityEquidistantOffset} from '../../offset/SketchEntityEquidistantOffset';
import {SketchExtensionAndIntersectionProcessing} from '../../offset/SketchExtensionAndIntersectionProcessing';
import {SketchOffsetCoverFactory} from '../../offset/SketchOffsetCoverFactory';
import type {OffsetCandidate, OffsetChainRecord, SketchEntityChain} from '../../offset/type';
import type {SketchDimension} from '../model/SketchDimension';
import type {SketchDimensionRepository} from '../repository/SketchDimensionRepository';

/**
 * 等距链派生同步服务端口。
 */
export interface OffsetChainDerivedSyncServicePort {
    /** 尺寸仓库（链首距离尺寸 metadata 是等距链的持久纽带）。 */
    readonly repository: SketchDimensionRepository;
    /** 按草图持久 ID 解析草图组。 */
    readonly resolveSketchGroup: (sketchId: string) => SketchGroup | null;
    /** 输出用户可见提示（自相交保护等）。 */
    readonly notifyNotice?: (message: string, level: 'warning') => void;
    /** 请求渲染刷新。 */
    readonly requestRender?: () => void;
}

/** 派生重算支持的链首尺寸类型。 */
const OFFSET_CHAIN_DIMENSION_KINDS: ReadonlySet<string> = new Set([
    'line-line-distance',
    'curve-offset-distance',
]);

/**
 * 等距链派生同步服务。
 *
 * 职责（单一）：
 * 1. 以链首距离尺寸（`line-line-distance` / `curve-offset-distance`）的
 *    `metadata.offsetChain` 为持久纽带，把源链与等距链配成偏移链；
 * 2. 修改链首距离尺寸值时没有整链几何写回，本服务按新名义距离对整条链执行
 *    与创建路径同口径的偏移 + 延伸/相交处理，再静默写回各等距实体，保证
 *    组合图形改尺寸时整条等距轮廓一起移动（对齐 SolidWorks 等距实体行为）；
 * 3. 整链重算成功后，把链内其余实体对的 driving 距离尺寸（创建时隐藏的
 *    `line-line-distance` / `curve-offset-distance`）名义值同步为新距离——
 *    这些尺寸仍是拖拽求解岛里的硬约束，不同步会在下次求解（如拖动端点）时
 *    把对应等距实体拉回旧距离；
 * 4. 自相交/整链失效保护：重算失败时保留上一有效形状并提示，且不修改链内
 *    其余尺寸的名义值（保持名义值与几何一致）；
 * 5. 写回完成后刷新受影响的闭合集合上色轮廓（等距链与盖子静默写回不触发
 *    轮廓刷新，加盖闭环的填充面必须随距离改值同步更新）。
 */
export class OffsetChainDerivedSyncService {
    /**
     * @param port 宿主端口。
     */
    public constructor(private readonly port: OffsetChainDerivedSyncServicePort) {
    }

    /**
     * 尺寸领域变更（修改名义值等）后，按新名义距离同步草图内全部等距链。
     *
     * 说明：修改链首距离尺寸值时，求解器只驱动链首实体；这里从尺寸 metadata
     * 恢复整条链，把源链作为权威侧、等距链按新名义值整链重算并静默写回。
     *
     * @param sketchId 草图持久 ID。
     */
    public synchronizeAfterDimensionChange(sketchId: string): void {
        this.forEachActiveOffsetChain(sketchId, (dimension, record) => {
            // 只有整链几何重算成功（当前几何与新名义距离一致）时才同步链内
            // 其余尺寸的名义值；重算失败保留旧几何，旧名义值也必须保持不变。
            if (this.synchronizeChain(dimension, record)) {
                this.synchronizePeerDimensionNominals(dimension, record);
            }
        });
    }

    /**
     * 遍历草图内全部活跃的等距链并回调。
     *
     * @param sketchId 草图持久 ID。
     * @param visitor 链回调。
     */
    private forEachActiveOffsetChain(
        sketchId: string,
        visitor: (dimension: SketchDimension, record: OffsetChainRecord) => void,
    ): void {
        this.port.repository.listBySketch(sketchId).forEach((dimension) => {
            const snapshot = dimension.toSnapshot();

            if (!OFFSET_CHAIN_DIMENSION_KINDS.has(snapshot.dimensionKind)
                || snapshot.role !== 'driving'
                || snapshot.suppressed
                || snapshot.solveState === 'dangling') {
                return;
            }

            const record = snapshot.metadata?.offsetChain;

            // 单实体链由求解器（直线/弧/圆/椭圆）或样条派生同步直接驱动，
            // 本服务只处理需要整链联动的多实体组合图形
            if (!record
                || record.sourceEntityIds.length <= 1
                || record.offsetEntityIds.length !== record.sourceEntityIds.length) {
                return;
            }

            visitor(dimension, record);
        });
    }

    /**
     * 重算一条等距链并静默写回。
     *
     * @param dimension 链首距离尺寸（新名义值为权威距离）。
     * @param record 等距链记录。
     * @returns 整链重算并写回成功返回 true；失败保留上一形状并返回 false。
     */
    private synchronizeChain(dimension: SketchDimension, record: OffsetChainRecord): boolean {
        try {
            return this.synchronizeChainUnchecked(dimension, record);
        } catch (error) {
            // 派生重算失败不得打断尺寸领域事件；保留上一形状并输出可定位日志
            this.port.notifyNotice?.(
                '等距链派生重算失败，保留当前形状。',
                'warning',
            );
            console.warn(`[等距链派生同步] 尺寸 ${dimension.dimensionId} 重算失败`, error);
            return false;
        }
    }

    /**
     * 把链内其余实体对的 driving 距离尺寸名义值同步为链首尺寸的新名义值。
     *
     * 说明：
     * 1. 组合图形等距链创建时，每对实体都创建了独立的 driving 距离尺寸
     *    （链首显示 UI，其余 `display.hidden` 隐藏），整链重算只写几何、
     *    不写尺寸仓库；隐藏尺寸名义值若停留在创建距离，下一次参数求解
     *    （如拖动端点）会把该实体对拉回旧距离；
     * 2. 各实体对的距离尺寸（平行线垂距 / 半径差 / 半轴差）都测量同一偏移
     *    距离，因此统一写入链首尺寸当前名义值即可；方向分支符号由重算几何
     *    与原分支共同保证，不做改写；
     * 3. 只处理可写的 driving 尺寸：只读、抑制、悬空或引用与链记录不匹配的
     *    尺寸一律跳过；
     * 4. 单条同步失败不得打断整条链的其余同步与尺寸领域事件：记录可定位日志
     *    并继续处理剩余实体对。
     *
     * @param dimension 链首距离尺寸（其 `nominalValue` 为权威距离）。
     * @param record 等距链记录。
     */
    private synchronizePeerDimensionNominals(
        dimension: SketchDimension,
        record: OffsetChainRecord,
    ): void {
        // 链记录按序配对（源[i] → 等距[i]），构建引用指纹查找表
        const offsetEntityIdBySourceId = new Map<string, string>();
        for (let index = 0; index < record.sourceEntityIds.length; index += 1) {
            const sourceEntityId = record.sourceEntityIds[index];
            const offsetEntityId = record.offsetEntityIds[index];

            if (sourceEntityId && offsetEntityId) {
                offsetEntityIdBySourceId.set(sourceEntityId, offsetEntityId);
            }
        }

        const targetNominal = dimension.nominalValue;

        this.port.repository.listBySketch(dimension.sketchId).forEach((candidate) => {
            const snapshot = candidate.toSnapshot();

            // 链首尺寸本身已是新名义值；只处理链内其余实体对的同类距离尺寸
            if (snapshot.dimensionId === dimension.dimensionId
                || !OFFSET_CHAIN_DIMENSION_KINDS.has(snapshot.dimensionKind)
                || snapshot.role !== 'driving'
                || snapshot.suppressed
                || snapshot.solveState === 'dangling'
                || snapshot.readOnly) {
                return;
            }

            // 引用顺序与创建口径一致：selectionOrder 0 = 源实体，1 = 等距实体
            const sourceEntityId = snapshot.references[0]?.entityId;
            const offsetEntityId = snapshot.references[1]?.entityId;

            if (!sourceEntityId || offsetEntityIdBySourceId.get(sourceEntityId) !== offsetEntityId) {
                return;
            }

            // 名义值已一致时不产生仓库事件，避免无意义的历史/观察者噪音
            if (snapshot.parameter.nominalValue === targetNominal) {
                return;
            }

            try {
                this.port.repository.mutate(dimension.sketchId, snapshot.dimensionId, (peer) => {
                    peer.changeNominalValue(targetNominal);
                });
            } catch (error) {
                this.port.notifyNotice?.(
                    '等距链距离尺寸名义值同步失败，该实体对可能在下一次求解时回到旧距离。',
                    'warning',
                );
                console.warn(
                    `[等距链派生同步] 尺寸 ${snapshot.dimensionId} 名义值同步到 `
                    + `${targetNominal} 失败（链首 ${dimension.dimensionId}）`,
                    error,
                );
            }
        });
    }

    /**
     * 重算一条等距链并静默写回（异常向上抛出，由 synchronizeChain 兜底）。
     *
     * 说明：解析失败、偏移自相交等业务性失败通过返回值区分，不做异常抛出——
     * 这类失败属于"保留上一形状"的领域结局，链内其余尺寸名义值也必须保持
     * 与几何一致（不触发同步）。
     *
     * @param dimension 链首距离尺寸（新名义值为权威距离）。
     * @param record 等距链记录。
     * @returns 整链已按新名义距离写回返回 true；保留上一形状返回 false。
     */
    private synchronizeChainUnchecked(dimension: SketchDimension, record: OffsetChainRecord): boolean {
        const sketchId = dimension.sketchId;
        const group = this.port.resolveSketchGroup(sketchId);

        if (!group) {
            return false;
        }

        // 解析源链与等距链（持久 ID 稳定，撤销/重做/加载后仍可解析）
        const sourceChain = this.resolveChain(group, record.sourceEntityIds);
        const offsetChain = this.resolveChain(group, record.offsetEntityIds);

        if (sourceChain.length !== record.sourceEntityIds.length
            || offsetChain.length !== record.offsetEntityIds.length
            || sourceChain.length !== offsetChain.length) {
            this.port.notifyNotice?.(
                '等距链记录无法完整解析，保留当前形状。',
                'warning',
            );
            return false;
        }

        const refPlane = sourceChain[0]?.referencePlane;

        if (!refPlane) {
            return false;
        }

        // 核心单位米 → 创建路径口径毫米
        const distanceMm = dimension.nominalValue * 1000;
        const newOffsetChain = this.rebuildOffsetChain(
            sourceChain,
            distanceMm,
            record.globalIsReverse,
            refPlane,
        );

        if (!newOffsetChain) {
            this.port.notifyNotice?.(
                '等距距离导致轮廓自相交或无法偏移，保留上一有效形状。',
                'warning',
            );
            return false;
        }

        if (newOffsetChain.length !== offsetChain.length) {
            this.port.notifyNotice?.(
                '等距链实体数量与记录不一致，跳过同步。',
                'warning',
            );
            this.disposeTransientChain(newOffsetChain);
            return false;
        }

        // 按索引静默写回各等距实体（不替换对象，保持 persistentId 与已挂约束）
        try {
            newOffsetChain.forEach((source, index) => {
                this.writeBackEntity(offsetChain[index]!, source);
            });
        } finally {
            // 临时链未挂场景，写回后释放几何（材质来自共享工厂，不 dispose）
            this.disposeTransientChain(newOffsetChain);
        }

        // 顶端加盖联动：偏移实体端点已随写回更新，这里按记录重建盖子实体几何
        this.rebuildCaps(group, record);

        // 闭合轮廓随动：链与盖子几何均为静默写回（不触发轮廓刷新），
        // 受影响的闭合集合上色轮廓需在此统一原地刷新
        this.refreshClosedProfileContours(group, record);

        this.port.requestRender?.();

        return true;
    }

    /**
     * 距离尺寸改值后按等距链记录重建盖子几何（顶端加盖联动）。
     *
     * 说明：
     * 1. 直线盖：两端点共享偏移实体端点（已随 writeBackEntity 更新位置），
     *    只需按最新端点重建线段几何；
     * 2. 圆弧盖：两端点同样已更新，这里按当前端点位置重算圆心/半径/角度
     *    并刷新弧几何（保留创建时的旋转方向，胶囊弯曲侧不变）；
     * 3. 双向偏移的盖子同时登记在左右两条链的记录上（local/partner 互换），
     *    对侧端点按当前值解析，任一侧距离尺寸改值都能重建盖子；
     * 4. 端点实体（源端点/配对链端点）解析失败或类型不匹配时保留当前形状并提示。
     *
     * @param group 活动草图组（按持久 ID 解析盖实体与端点）。
     * @param record 等距链记录。
     */
    private rebuildCaps(group: SketchGroup, record: OffsetChainRecord): void {
        const caps = record.caps;
        if (!caps || caps.length === 0) {
            return;
        }

        for (const cap of caps) {
            const cover = group.getObjectByPersistentId(cap.capEntityId);
            const localEndpoint = group.getObjectByPersistentId(cap.localEndpointId);
            const partnerEndpoint = group.getObjectByPersistentId(cap.partnerEndpointId);
            if (!isSketchLineObject(cover) && !isSketchArcObject(cover)) {
                this.port.notifyNotice?.('等距链盖子实体解析失败，保留当前形状。', 'warning');
                continue;
            }
            if (!isSketchPointObject(localEndpoint) || !isSketchPointObject(partnerEndpoint)) {
                this.port.notifyNotice?.('等距链盖子端点解析失败，保留当前形状。', 'warning');
                continue;
            }

            if (cap.capType === 'straight line' && isSketchLineObject(cover)) {
                // 端点共享已随链写回更新位置，这里只重建线段几何
                cover.refreshGeometryOnly();
            } else if (cap.capType === 'circularArc' && isSketchArcObject(cover)) {
                SketchOffsetCoverFactory.updateArcCoverGeometry(cover, localEndpoint, partnerEndpoint);
            }
        }
    }

    /**
     * 刷新受等距链写回影响的闭合集合上色轮廓。
     *
     * 说明：
     * 1. `writeBackEntity` 使用静默写回接口（syncPositionSilently / refreshGeometryOnly
     *    等），不会触发轮廓刷新；距离改值后加盖闭环的填充面必须在此原地重建；
     * 2. 闭合拓扑不随距离改值变化（链与盖子始终共享端点对象），只需刷新几何，
     *    无需重跑闭合收口；
     * 3. 关联判定用「集合成员 ∩ (等距链 ∪ 盖子)」而非源链：闭合集合一定包含
     *    等距链成员（单向加盖集合同时包含源链），不会漏判；
     * 4. 集合未创建轮廓（上色开关关闭时）由 refreshContourGeometry 内部 no-op，
     *    无需额外开关判断。
     *
     * @param group 活动草图组（集合对象宿主）。
     * @param record 等距链记录。
     */
    private refreshClosedProfileContours(group: SketchGroup, record: OffsetChainRecord): void {
        // 本记录直接改写的几何对象：等距链成员 + 顶端加盖实体
        const affectedPersistentIds = new Set<string>([
            ...record.offsetEntityIds,
            ...(record.caps ?? []).map((cap) => cap.capEntityId),
        ]);

        group.getSketchCollectionObjects().forEach((collection) => {
            const isRelated = Array.from(collection.collectSet)
                .some((source) => affectedPersistentIds.has(source.persistentId));
            if (isRelated) {
                collection.refreshContourGeometry();
            }
        });
    }

    /**
     * 按创建路径同口径重建等距链：逐实体偏移 → 延伸/相交处理。
     *
     * @param sourceChain 源链（权威侧）。
     * @param distanceMm 偏移距离（毫米）。
     * @param globalIsReverse 全局反向开关（回放创建时的方向口径）。
     * @param refPlane 草图参考面。
     * @returns 处理后的等距链；失败返回 null。
     */
    private rebuildOffsetChain(
        sourceChain: SketchEntityChain,
        distanceMm: number,
        globalIsReverse: boolean,
        refPlane: ReferencePlane,
    ): SketchEntityChain | null {
        // 与 SketchOffsetController.generatePreviewInDirection 阶段一同一口径
        const effectiveReverseMap = CombineSketchEntitiesUnifiedDirection
            .computeChainEffectiveReverseFlags(sourceChain, refPlane, globalIsReverse);

        const offsetChain: OffsetCandidate[] = [];
        for (const entity of sourceChain) {
            const effectiveIsReverse = effectiveReverseMap.get(entity.uuid) ?? globalIsReverse;
            const offsetEntity = this.offsetSingle(entity, distanceMm, effectiveIsReverse, refPlane);

            if (!offsetEntity) {
                // 向内偏移超过半径/半轴等失败：整链保留上一形状
                this.disposeTransientChain(offsetChain);
                return null;
            }
            offsetChain.push(offsetEntity);
        }

        const closedFlags = [CombineSketchEntitiesUnifiedDirection.isChainClosed(sourceChain)];
        const keptOriginalChains: SketchEntityChain[] = [];
        const processedChains = SketchExtensionAndIntersectionProcessing
            .sketchExtensionAndIntersectionProcessing(
                [offsetChain],
                [sourceChain],
                closedFlags,
                keptOriginalChains,
            );

        if (processedChains.length === 0 || processedChains[0]!.length === 0) {
            this.disposeTransientChain(offsetChain);
            return null;
        }

        // 延伸/相交处理原位替换链内实体：原始临时偏移链已被丢弃，释放其几何
        this.disposeTransientChain(offsetChain);

        return processedChains[0]!;
    }

    /**
     * 对单个源实体执行偏移（与 SketchOffsetController.offsetSingleEntity 同口径）。
     *
     * @param entity 源实体。
     * @param distanceMm 偏移距离（毫米）。
     * @param isReverse 该实体的有效反向开关。
     * @param refPlane 草图参考面。
     * @returns 偏移后的实体；失败返回 null。
     */
    private offsetSingle(
        entity: OffsetCandidate,
        distanceMm: number,
        isReverse: boolean,
        refPlane: ReferencePlane,
    ): OffsetCandidate | null {
        if (isSketchLineObject(entity)) {
            return SketchEntityEquidistantOffset.isometricSketchLineObject(
                entity,
                distanceMm,
                isReverse,
                refPlane,
            );
        }
        if (isSketchArcObject(entity)) {
            return SketchEntityEquidistantOffset.isometricSketchArcObject(
                entity,
                distanceMm,
                isReverse,
                refPlane,
            );
        }
        if (isSketchEllipseObject(entity)) {
            return SketchEntityEquidistantOffset.isometricSketchEllipticalArc(
                entity,
                distanceMm,
                isReverse,
                refPlane,
            );
        }
        if (isSketchParabolaObject(entity)) {
            return SketchEntityEquidistantOffset.isometricSketchParabolaObject(
                entity,
                distanceMm,
                isReverse,
                refPlane,
            );
        }
        if (isSketchSplineObject(entity) || isSketchStyleSplineObject(entity)) {
            return SketchEntityEquidistantOffset.isometricSketchSplineCurve(
                entity,
                distanceMm,
                isReverse,
                refPlane,
            );
        }
        if (isSketchCircleObject(entity)) {
            return SketchEntityEquidistantOffset.isometricSketchCircleObject(
                entity,
                distanceMm,
                isReverse,
                refPlane,
            );
        }
        return null;
    }

    /**
     * 把重算后的新几何静默写回旧等距实体（保持对象身份）。
     *
     * @param target 旧等距实体（落地后仍在草图组内）。
     * @param source 重算产生的新等距实体（临时，写回后释放）。
     */
    private writeBackEntity(target: OffsetCandidate, source: OffsetCandidate): void {
        if (isSketchLineObject(target) && isSketchLineObject(source)) {
            target.startPoint.syncPositionSilently(source.startPoint.position.clone());
            target.endPoint.syncPositionSilently(source.endPoint.position.clone());
            target.refreshGeometryOnly();
            return;
        }
        if (isSketchArcObject(target) && isSketchArcObject(source)) {
            target.updateArcDefinition(source.getArcDefinition());
            return;
        }
        if (isSketchCircleObject(target) && isSketchCircleObject(source)) {
            target.setRadiusSilently(source.radius);
            return;
        }
        if (isSketchEllipseObject(target) && isSketchEllipseObject(source)) {
            target.updateEllipseDefinition(source.getEllipseDefinition());
            return;
        }
        if (isSketchParabolaObject(target) && isSketchParabolaObject(source)) {
            target.updateParabolaDefinition(source.getParabolaDefinition());
            return;
        }
        // 旧版等距抛物线兼容分支：新几何口径下抛物线偏移输出样条，
        // 旧工程中已落地的等距抛物线无法跨类型静默写回，
        // 明确告警避免距离改值后联动静默失效。
        if (isSketchParabolaObject(target) && isSketchSplineObject(source)) {
            this.port.notifyNotice?.(
                '旧版等距抛物线无法按新几何口径联动，请删除后重新创建等距实体。',
                'warning',
            );
            return;
        }
        if ((isSketchSplineObject(target) || isSketchStyleSplineObject(target))
            && (isSketchSplineObject(source) || isSketchStyleSplineObject(source))) {
            this.writeBackSpline(
                target as SketchSplineObject,
                source as SketchSplineObject,
            );
        }
    }

    /**
     * 写回样条/样式样条：控制点静默写入 + 曲线点列以重算结果为权威。
     *
     * @param target 旧等距样条。
     * @param source 重算产生的新等距样条。
     */
    private writeBackSpline(target: SketchSplineObject, source: SketchSplineObject): void {
        const targetControlPoints = target.getControlPoints();
        const sourceControlPoints = source.getControlPoints();

        if (targetControlPoints.length !== sourceControlPoints.length) {
            this.port.notifyNotice?.(
                '等距样条控制点数量与几何结果不一致，跳过同步。',
                'warning',
            );
            return;
        }

        // 控制点位置逐个静默写入（保持点对象身份，不触发约束联动）
        sourceControlPoints.forEach((point, index) => {
            targetControlPoints[index]!.syncPositionSilently(point.getPosition());
        });
        // 曲线点列以重算结果为权威（与创建路径同一几何口径）
        target.updateCurvePoints(source.getCurvePoints().map((point) => point.clone()));
    }

    /**
     * 按持久 ID 解析链实体。
     *
     * @param group 草图组。
     * @param entityIds 实体持久 ID 列表。
     * @returns 可参与等距链的实体列表（跳过无法解析或类型不匹配的 ID）。
     */
    private resolveChain(group: SketchGroup, entityIds: readonly string[]): SketchEntityChain {
        const chain: SketchEntityChain = [];

        for (const entityId of entityIds) {
            const target = group.getObjectByPersistentId(entityId);
            const candidate = this.asOffsetCandidate(target);

            if (candidate) {
                chain.push(candidate);
            }
        }

        return chain;
    }

    /**
     * 把运行时对象收窄为等距链支持的草图实体。
     *
     * @param target 运行时对象。
     * @returns 等距链支持的草图实体；类型不匹配返回 null。
     */
    private asOffsetCandidate(target: unknown): OffsetCandidate | null {
        if (isSketchLineObject(target)
            || isSketchArcObject(target)
            || isSketchCircleObject(target)
            || isSketchEllipseObject(target)
            || isSketchParabolaObject(target)
            || isSketchSplineObject(target)
            || isSketchStyleSplineObject(target)) {
            return target as OffsetCandidate;
        }

        return null;
    }

    /**
     * 释放临时链几何（临时实体未挂场景，只释放 BufferGeometry；材质来自共享工厂）。
     *
     * @param chain 临时等距链。
     */
    private disposeTransientChain(chain: readonly OffsetCandidate[]): void {
        for (const entity of chain) {
            const geometry = (entity as unknown as {geometry?: {dispose?: () => void}}).geometry;

            geometry?.dispose?.();
        }
    }
}
